import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { SkeletonCard, SkeletonOrderRow } from '../components/Skeleton';
import { PageHeader } from '../components/Form';
import Modal from '../components/Modal';
import Icon from '../components/Icon';

const AVATAR_KEY = 'user_avatar';

const AVATAR_OPTIONS = [
  'user', 'user-ninja', 'user-astronaut', 'user-tie', 'user-secret',
  'user-md', 'user-graduate', 'robot', 'ghost', 'cat',
  'dog', 'chess-king', 'chess-queen', 'fire', 'gem',
  'trophy', 'rocket', 'star', 'crown', 'skull',
  'paw', 'gamepad', 'dice', 'bolt',
];

interface UserProfile { id: string; fullName: string; username: string; email: string | null; phone: string | null; role: string }
interface TopUpOrder {
  id: string; orderNumber: string; amount: number | string; status: string;
  reason?: string | null; createdAt: string;
  target?: 'game' | 'wallet';
  paymentSource?: 'method' | 'wallet';
  game?: { name: string } | null;
  paymentMethod?: { name: string } | null;
}
interface WithdrawOrder {
  id: string; orderNumber: string; amount: number | string; status: string;
  reason?: string | null; payoutTag: string; createdAt: string;
  source?: 'game' | 'wallet';
  paymentMethod?: { name: string } | null;
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  Processing: { bg: '#fef3c7', text: '#92400e' },
  Verified:   { bg: '#ccfbf1', text: '#0f766e' },
  Completed:  { bg: '#dcfce7', text: '#166534' },
  Paid:       { bg: '#dcfce7', text: '#166534' },
  Rejected:   { bg: '#fee2e2', text: '#991b1b' },
  Cancelled:  { bg: '#f1f5f9', text: '#475569' },
  Pending:    { bg: '#fef3c7', text: '#92400e' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] || { bg: '#f1f5f9', text: '#475569' };
  return (
    <div style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: s.bg }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: s.text }}>{status}</span>
    </div>
  );
}

function toMoney(value: number | string | null | undefined) {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
}

function orderHistoryTitle(item: TopUpOrder | WithdrawOrder, tab: 'topup' | 'withdraw') {
  if (tab === 'topup') {
    const order = item as TopUpOrder;
    if (order.target === 'wallet') return 'Wallet Top-Up';
    if (order.paymentSource === 'wallet') return order.game?.name ? `${order.game.name} via Wallet` : 'Game Top-Up via Wallet';
    return order.game?.name || order.paymentMethod?.name || 'Top-Up';
  }
  const order = item as WithdrawOrder;
  const destination = order.payoutTag || order.paymentMethod?.name || 'Withdrawal';
  return order.source === 'wallet' ? `Wallet Withdrawal to ${destination}` : destination;
}

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { isDark, colors, toggleTheme } = useTheme();
  const { alert } = useAlert();
  const toast = useToast();
  const { orderUpdateCount } = useSocket();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [avatarIcon, setAvatarIcon] = useState('user');

  const [showHistory, setShowHistory] = useState(false);
  const [historyTab, setHistoryTab] = useState<'topup' | 'withdraw'>('topup');
  const [topUps, setTopUps] = useState<TopUpOrder[]>([]);
  const [withdraws, setWithdraws] = useState<WithdrawOrder[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const prevStatusesRef = useRef<Record<string, string>>({});

  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [policyContent, setPolicyContent] = useState('');
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyLoaded, setPolicyLoaded] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const profileRes = await api.get<UserProfile>('/auth/me');
      setProfile(profileRes.data);
      const savedAvatar = localStorage.getItem(AVATAR_KEY);
      if (savedAvatar) setAvatarIcon(savedAvatar);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const fetchHistory = useCallback(async (showSpinner = false) => {
    if (showSpinner) setHistoryLoading(true);
    try {
      const [tu, wd] = await Promise.all([
        api.get<TopUpOrder[]>('/orders/topup/my'),
        api.get<WithdrawOrder[]>('/orders/withdraw/my'),
      ]);
      const allOrders = [...tu.data, ...wd.data];
      const prev = prevStatusesRef.current;
      let changed = false;
      allOrders.forEach(o => {
        if (prev[o.id] && prev[o.id] !== o.status) changed = true;
        prev[o.id] = o.status;
      });
      if (changed && navigator.vibrate) navigator.vibrate([0, 200, 100, 200]);
      prevStatusesRef.current = prev;
      setTopUps(tu.data);
      setWithdraws(wd.data);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  }, []);

  const openHistory = useCallback(async () => {
    setShowHistory(true);
    await fetchHistory(true);
  }, [fetchHistory]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('openHistory') === '1') {
      openHistory();
      navigate('/profile', { replace: true });
    }
  }, [location.search, openHistory, navigate]);

  useEffect(() => {
    if (!showHistory) return;
    fetchHistory(false);
  }, [orderUpdateCount, showHistory, fetchHistory]);

  const selectAvatar = (icon: string) => {
    setAvatarIcon(icon);
    setShowAvatarPicker(false);
    try { localStorage.setItem(AVATAR_KEY, icon); } catch { /* silent */ }
  };

  const openPrivacyPolicy = async () => {
    setShowPrivacyPolicy(true);
    if (policyLoaded) return;
    setPolicyLoading(true);
    try {
      const res = await api.get<{ content: string }>('/privacy-policy');
      setPolicyContent(res.data.content || 'No privacy policy has been published yet.');
      setPolicyLoaded(true);
    } catch {
      setPolicyContent('Unable to load the Privacy Policy. Please try again later.');
    } finally {
      setPolicyLoading(false);
    }
  };

  const handleLogout = () => {
    alert('Sign Out', 'Are you sure you want to sign out?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Sign Out',
        style: 'destructive',
        onPress: () => {
          logout().catch(err => alert('Sign Out Failed', err?.message || 'Please try again.'));
        },
      },
    ]);
  };

  const copyOrder = async (orderNumber: string) => {
    try {
      await navigator.clipboard.writeText(orderNumber);
      if (navigator.vibrate) navigator.vibrate(40);
      toast.success('Copied', orderNumber);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader
        icon="user-circle"
        title="My Profile"
        right={
          <button
            aria-label="Open notifications"
            onClick={() => navigate('/notifications')}
            style={{
              width: 44, height: 44, borderRadius: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', cursor: 'pointer',
            }}
          >
            <Icon name="bell" size={18} color={colors.text} />
          </button>
        }
      />

      {loading ? (
        <div style={{ padding: 16 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <div style={{ padding: 16, paddingBottom: 40 }}>
          {/* Profile Card */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: 16, borderRadius: 16,
            backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
            marginBottom: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          }}>
            <button
              aria-label="Change avatar"
              onClick={() => setShowAvatarPicker(true)}
              style={{
                position: 'relative',
                width: 56, height: 56, borderRadius: 28,
                backgroundColor: '#14b8a6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', flexShrink: 0,
              }}
            >
              <Icon name={avatarIcon} size={26} color="#fff" />
              <span style={{
                position: 'absolute', bottom: 0, right: 0,
                width: 18, height: 18, borderRadius: 9,
                backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid #fff',
              }}>
                <Icon name="pencil-alt" size={8} color="#fff" />
              </span>
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>
                {profile?.fullName || 'Player'}
              </div>
              <div style={{ fontSize: 13, color: colors.textSub, marginTop: 1 }}>
                @{profile?.username || '—'}
              </div>
              {profile?.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                  <Icon name="envelope" size={11} color={colors.textSub} />
                  <span style={{ fontSize: 12, color: colors.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {profile.email}
                  </span>
                </div>
              )}
            </div>
            <div style={{
              padding: '4px 8px', borderRadius: 20,
              backgroundColor: '#ccfbf1',
              alignSelf: 'flex-start', flexShrink: 0,
            }}>
              <span style={{ color: '#0f766e', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
                {(profile?.role || 'player').toUpperCase()}
              </span>
            </div>
          </div>

          <SectionLabel>Account</SectionLabel>
          <MenuRow icon="history" label="Order History" desc="Top-ups, withdrawals, status timeline" onClick={openHistory} />
          <MenuRow icon="bell" label="Notification Preferences" desc="Choose what we notify you about" onClick={() => navigate('/notification-prefs')} />
          <MenuRow icon="question-circle" label="FAQ" desc="Common questions and answers" onClick={() => navigate('/faq')} />
          <MenuRow icon="smile" label="Change Avatar" desc="Pick an icon to represent you" onClick={() => setShowAvatarPicker(true)} />

          <SectionLabel style={{ marginTop: 20 }}>Preferences</SectionLabel>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: 14, borderRadius: 14, marginBottom: 8,
              backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              minHeight: 72,
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              backgroundColor: colors.card,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name={isDark ? 'moon' : 'sun'} size={15} color={isDark ? '#818cf8' : '#f59e0b'} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>Dark Mode</div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 1 }}>
                {isDark ? 'Switch to light' : 'Switch to dark'}
              </div>
            </div>
            <ThemeSwitch checked={isDark} onChange={toggleTheme} />
          </div>

          <SectionLabel style={{ marginTop: 20 }}>Legal</SectionLabel>
          <MenuRow icon="shield-alt" label="Privacy Policy" desc="How we handle your data" onClick={openPrivacyPolicy} />

          <SectionLabel style={{ marginTop: 20 }}>Session</SectionLabel>
          <button
            onClick={handleLogout}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: 14, borderRadius: 14, marginBottom: 8,
              backgroundColor: colors.card, border: '1px solid #fecdd3',
              cursor: 'pointer', textAlign: 'left', minHeight: 72,
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              backgroundColor: '#fff1f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="sign-out-alt" size={15} color="#ef4444" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444' }}>Sign Out</div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 1 }}>You'll need to sign in again</div>
            </div>
          </button>
        </div>
      )}

      {/* Order History */}
      <Modal open={showHistory} onClose={() => setShowHistory(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>Order History</div>
          <button
            aria-label="Close"
            onClick={() => setShowHistory(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textSub} />
          </button>
        </div>

        <div style={{
          display: 'flex', backgroundColor: colors.bg,
          borderRadius: 10, padding: 4, marginBottom: 14,
        }}>
          {(['topup', 'withdraw'] as const).map(tab => {
            const active = historyTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setHistoryTab(tab)}
                style={{
                  flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5,
                  padding: '8px 0', borderRadius: 8, border: 'none',
                  backgroundColor: active ? colors.card : 'transparent',
                  cursor: 'pointer', minHeight: 44,
                  boxShadow: active ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                }}
              >
                <Icon name={tab === 'topup' ? 'arrow-up' : 'arrow-down'} size={12} color={active ? colors.text : colors.textMuted} />
                <span style={{
                  fontSize: 13, fontWeight: active ? 700 : 600,
                  color: active ? colors.text : '#94a3b8',
                }}>
                  {tab === 'topup' ? 'Top-Ups' : 'Withdrawals'}
                </span>
              </button>
            );
          })}
        </div>

        {historyLoading ? (
          <div style={{ padding: '8px 0' }}>
            {[1,2,3].map(i => <SkeletonOrderRow key={i} />)}
          </div>
        ) : (
          <div style={{ maxHeight: 460, overflowY: 'auto' }}>
            {(historyTab === 'topup' ? topUps : withdraws).length === 0 ? (
              <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <Icon name="inbox" size={32} color={colors.textMuted} />
                <div style={{ color: colors.textMuted, marginTop: 6 }}>No orders yet</div>
              </div>
            ) : (historyTab === 'topup' ? topUps : withdraws).map(item => (
              <button
                key={item.id}
                onClick={() => {
                  setShowHistory(false);
                  navigate(`/order/${historyTab}/${item.id}`);
                }}
                style={{
                  width: '100%', display: 'block',
                  borderRadius: 12, padding: 12, marginBottom: 8,
                  backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`,
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div
                    onClick={e => { e.stopPropagation(); copyOrder(item.orderNumber); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, paddingRight: 8, cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors.textSub }}>{item.orderNumber}</span>
                    <Icon name="copy" size={11} color={colors.textMuted} />
                  </div>
                  <StatusBadge status={item.status} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, marginBottom: 8 }}>
                  {orderHistoryTitle(item, historyTab)}
                </div>
                {item.status === 'Rejected' && item.reason && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 5,
                    backgroundColor: '#fef2f2', borderRadius: 8,
                    padding: '6px 8px', marginBottom: 8,
                  }}>
                    <Icon name="exclamation-circle" size={11} color="#dc2626" />
                    <span style={{ flex: 1, color: '#991b1b', fontSize: 11, lineHeight: 1.45, fontWeight: 600 }}>
                      Reason: {item.reason}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 17, fontWeight: 800, color: colors.text }}>${toMoney(item.amount)}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="calendar-alt" size={11} color={colors.textMuted} />
                    <span style={{ fontSize: 12, color: colors.textMuted }}>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </Modal>

      {/* Avatar Picker */}
      <Modal open={showAvatarPicker} onClose={() => setShowAvatarPicker(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>Choose Avatar</div>
          <button
            aria-label="Close"
            onClick={() => setShowAvatarPicker(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textSub} />
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
          {AVATAR_OPTIONS.map(icon => {
            const active = avatarIcon === icon;
            return (
              <button
                key={icon}
                aria-label={`Select ${icon} avatar`}
                onClick={() => selectAvatar(icon)}
                style={{
                  width: 56, height: 56, borderRadius: 14,
                  border: `1.5px solid ${active ? '#14b8a6' : colors.cardBorder}`,
                  backgroundColor: active ? '#14b8a6' : colors.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Icon name={icon} size={22} color={active ? '#fff' : '#14b8a6'} />
              </button>
            );
          })}
        </div>
      </Modal>

      {/* Privacy Policy */}
      <Modal open={showPrivacyPolicy} onClose={() => setShowPrivacyPolicy(false)} contentStyle={{ maxHeight: '80%' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: colors.text }}>Privacy Policy</div>
          <button
            aria-label="Close"
            onClick={() => setShowPrivacyPolicy(false)}
            style={{
              width: 44, height: 44, borderRadius: 22,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginRight: -8,
            }}
          >
            <Icon name="times" size={18} color={colors.textSub} />
          </button>
        </div>
        {policyLoading ? (
          <SkeletonCard />
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <div style={{ fontSize: 14, lineHeight: 1.7, color: colors.textSub, whiteSpace: 'pre-wrap' }}>
              {policyContent}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const { colors } = useTheme();
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: 1,
      color: colors.sectionLabel, marginBottom: 8, marginTop: 4,
      textTransform: 'uppercase',
      ...style,
    }}>
      {children}
    </div>
  );
}

function MenuRow({ icon, label, desc, onClick }: { icon: string; label: string; desc: string; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: 14, borderRadius: 14, marginBottom: 8,
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        cursor: 'pointer', textAlign: 'left', minHeight: 72,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={15} color={colors.accentText} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{label}</div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 1 }}>{desc}</div>
      </div>
      <Icon name="chevron-right" size={13} color={colors.textMuted} />
    </button>
  );
}

function ThemeSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      style={{
        position: 'relative', width: 50, height: 30, borderRadius: 15,
        backgroundColor: checked ? '#14b8a6' : '#e2e8f0',
        border: 'none', cursor: 'pointer', flexShrink: 0,
        transition: 'background-color 200ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 24, height: 24, borderRadius: 12,
          backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          transition: 'left 180ms ease',
        }}
      />
    </button>
  );
}
